import {
  Type,
  Hash,
  List,
  Calendar,
  CheckCircle,
  Percent,
  Camera,
  Mic,
  Grid3x3,
  Ruler,
  Leaf,
  Footprints,
  Timer,
  HelpCircle,
  LucideIcon,
} from "lucide-react";
import { EntryConfigType } from "@/types/entryConfig";

/**
 * Maps trait types to their corresponding Lucide React icons
 * Matches iOS app SF Symbols for platform consistency
 */
export const TRAIT_ICON_MAP: Record<EntryConfigType, LucideIcon> = {
  text: Type,
  number: Hash,
  category: List,
  date: Calendar,
  boolean: CheckCircle,
  percentage: Percent,
  photo: Camera,
  transcription: Mic,
  imageMeasurement: Camera,
  standCountMeasurement: Grid3x3,
  rowPlantedStandCount: Footprints,
  ruler: Ruler,
  leafAreaIndex: Leaf,
  timer: Timer,
};

/**
 * Optional color scheme matching iOS app chip colors
 * Can be used for additional visual distinction
 */
export const TRAIT_COLOR_MAP: Record<EntryConfigType, string> = {
  text: "text-orange-600",
  number: "text-blue-600",
  category: "text-purple-600",
  date: "text-green-600",
  boolean: "text-indigo-600",
  percentage: "text-teal-600",
  photo: "text-pink-600",
  transcription: "text-red-600",
  imageMeasurement: "text-cyan-600",
  standCountMeasurement: "text-amber-700",
  rowPlantedStandCount: "text-gray-600",
  ruler: "text-emerald-500",
  leafAreaIndex: "text-green-600",
  timer: "text-rose-600",
};

/**
 * Get the icon component for a given trait type
 * @param type - The entry config type
 * @returns The corresponding Lucide icon component
 */
export const getTraitIcon = (type: EntryConfigType): LucideIcon => {
  // Hardened against unknown future types (forward-compat with iOS unknown-type
  // tolerance): an unrecognized type falls back to a neutral icon instead of
  // returning undefined and crashing rendering.
  return TRAIT_ICON_MAP[type] ?? HelpCircle;
};

/**
 * Get the color class for a given trait type
 * @param type - The entry config type
 * @returns The Tailwind color class
 */
export const getTraitColor = (type: EntryConfigType): string => {
  // Hardened against unknown future types (see getTraitIcon).
  return TRAIT_COLOR_MAP[type] ?? "text-gray-500";
};

/**
 * Convert trait type enum to human-readable label
 * @param type - The entry config type
 * @returns Human-readable label
 */
export const getTraitTypeLabel = (type: EntryConfigType): string => {
  const labelMap: Record<EntryConfigType, string> = {
    text: "Text",
    number: "Number",
    category: "Category",
    date: "Date",
    boolean: "Yes / No",
    percentage: "Percentage",
    photo: "Photo",
    transcription: "Transcription",
    imageMeasurement: "Image Measurement",
    standCountMeasurement: "Stand Count Measurement",
    rowPlantedStandCount: "Row Planted Stand Count",
    ruler: "Ruler",
    leafAreaIndex: "Leaf Area Index",
    timer: "Stopwatch",
  };
  // Hardened against unknown future types (see getTraitIcon).
  return labelMap[type] ?? "Unknown";
};

/**
 * Sort traits/entry configs by type first, then by name
 * @param traits - Array of entry configs to sort
 * @returns Sorted array
 */
export const sortTraitsByTypeAndName = <T extends { type: EntryConfigType; name: string }>(
  traits: T[]
): T[] => {
  return [...traits].sort((a, b) => {
    // First sort by type
    if (a.type !== b.type) {
      return a.type.localeCompare(b.type);
    }
    // Then sort by name
    return a.name.localeCompare(b.name);
  });
};
